/**
 * The browser half of attachments (RFC 0288).
 *
 * mod-core owns the policy (what is too large, what shape the pointer takes)
 * and knows nothing about a DOM. This is the adapter: turning a `File` into the
 * three fields mod-core wants, and downscaling an image through a canvas.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import type {
  AttachmentDoc,
  AttachmentSource,
  ImageProcessor,
} from '@mod/core/model'
import { useEffect, useState } from 'react'

export async function fileToSource(file: File): Promise<AttachmentSource> {
  return {
    name: file.name,
    // A file dragged from some applications arrives with an empty type. Storing
    // "" would make it un-renderable later, and octet-stream at least reads as
    // "a file" rather than as a broken image.
    mimeType: file.type || 'application/octet-stream',
    bytes: new Uint8Array(await file.arrayBuffer()),
  }
}

/**
 * Formats a canvas cannot re-encode without destroying them.
 *
 * A GIF re-encoded through a canvas loses every frame but the first, and an SVG
 * rasterizes and stops being a vector. Both are stored untouched and unmeasured.
 */
const NOT_RE_ENCODABLE = new Set(['image/gif', 'image/svg+xml'])

/** Formats a canvas can write back out, so a downscale can keep the format. */
const RE_ENCODABLE = new Set(['image/jpeg', 'image/png', 'image/webp'])

/**
 * Downscale to fit `maxEdge`, preserving aspect ratio, and report the result.
 *
 * An image already within the limit is returned byte-identical rather than
 * re-encoded: re-encoding a small PNG through a canvas can make it BIGGER, and
 * a lossless original is worth more than a few saved bytes.
 */
export const browserProcessImage: ImageProcessor = async (
  bytes,
  mimeType,
  maxEdge,
) => {
  if (NOT_RE_ENCODABLE.has(mimeType)) {
    const measured = await measure(bytes, mimeType)
    return { bytes, mimeType, ...measured }
  }

  // A format this browser cannot decode (HEIC off an iPhone, TIFF, or simply a
  // corrupt file) must still attach. Storing the original bytes untouched costs
  // only the reserved layout; throwing here would refuse the file outright.
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(
      new Blob([bytes as BlobPart], { type: mimeType }),
    )
  } catch {
    return { bytes, mimeType, width: 0, height: 0 }
  }

  try {
    const { width, height } = bitmap
    const longest = Math.max(width, height)
    if (longest <= maxEdge) return { bytes, mimeType, width, height }

    const scale = maxEdge / longest
    const w = Math.max(1, Math.round(width * scale))
    const h = Math.max(1, Math.round(height * scale))

    const canvas = new OffscreenCanvas(w, h)
    const ctx = canvas.getContext('2d')
    if (!ctx) return { bytes, mimeType, width, height }
    ctx.drawImage(bitmap, 0, 0, w, h)

    // Keep the source format where the canvas can write it: a JPEG stays a
    // JPEG, a WebP stays a WebP. Anything else becomes PNG, which every canvas
    // can encode, rather than silently gaining a black background where a
    // transparent source was flattened into JPEG.
    const wanted = RE_ENCODABLE.has(mimeType) ? mimeType : 'image/png'
    const blob = await canvas.convertToBlob({ type: wanted, quality: 0.9 })
    return {
      bytes: new Uint8Array(await blob.arrayBuffer()),
      // The BLOB's type, not the one we asked for: convertToBlob falls back to
      // PNG for a type this browser cannot encode, and says so here. Reporting
      // the requested type instead would label PNG bytes as something else, and
      // the model's API rejects an image whose media_type contradicts its data.
      mimeType: blob.type || wanted,
      width: w,
      height: h,
    }
  } finally {
    bitmap.close()
  }
}

async function measure(
  bytes: Uint8Array,
  mimeType: string,
): Promise<{ width: number; height: number }> {
  try {
    const bitmap = await createImageBitmap(
      new Blob([bytes as BlobPart], { type: mimeType }),
    )
    const out = { width: bitmap.width, height: bitmap.height }
    bitmap.close()
    return out
  } catch {
    // An image the browser will not decode is still storable; it just renders
    // without reserved layout.
    return { width: 0, height: 0 }
  }
}

/** Pull files off a paste, ignoring the text/html and text/plain that ride along. */
export function filesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return []
  const out: File[] = []
  for (const item of Array.from(data.items)) {
    if (item.kind !== 'file') continue
    const file = item.getAsFile()
    if (file) out.push(file)
  }
  return out
}

/** True when a drag carries files, as opposed to a reordering drag. */
export function dragHasFiles(data: DataTransfer | null): boolean {
  if (!data) return false
  return Array.from(data.types).includes('Files')
}

/**
 * Resolve an attachment document to a URL an <img> can use.
 *
 * Cached by document id, module-wide, exactly as patchwork's chitter does it
 * (`lib/blob-cache.ts`). An attachment document is immutable once written, so
 * there is no invalidation to get wrong, and a thread that scrolls past the
 * same image repeatedly makes one object URL rather than one per mount.
 */
const blobUrls = new Map<string, string>()

export function useAttachmentUrl(
  repo: Repo | undefined,
  docId: string,
): string | null {
  const [url, setUrl] = useState<string | null>(
    () => blobUrls.get(docId) ?? null,
  )

  useEffect(() => {
    const hit = blobUrls.get(docId)
    if (hit) {
      setUrl(hit)
      return
    }
    if (!repo) return
    let live = true
    void (async () => {
      try {
        const handle = await repo.find(docId as AnyDocumentId)
        await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
        const doc = handle.doc() as AttachmentDoc | undefined
        if (!doc?.content) return
        // Automerge hands back its own view of the bytes; copying into a plain
        // Uint8Array is what makes Blob accept them across every adapter.
        const bytes = new Uint8Array(doc.content)
        const made = URL.createObjectURL(
          new Blob([bytes as BlobPart], { type: doc.mimeType }),
        )
        blobUrls.set(docId, made)
        if (live) setUrl(made)
      } catch {
        // A document that has not synced to this peer yet: the thread shows the
        // chip without a preview rather than an error, and a later mount (after
        // it arrives) resolves it.
      }
    })()
    return () => {
      live = false
    }
    // Deliberately NOT revoking on unmount: the url is shared through the cache
    // with every other mount of the same attachment, and revoking here would
    // break an image still on screen somewhere else.
  }, [repo, docId])

  return url
}
